import React from 'react'
import classes from '@src/styles/vs-competition.module.css'

export type Review = {
  score: number
  count: number
  /** The platform the score comes from — shown beside the stars. */
  source: string
  url: string
}

export type Provider = {
  name: string
  domain: string
  url: string
  coverage: string
  pricing: number
  review: Review | null
  bookOnline: string
}

export type ComparisonProps = {
  heading: string
  columns: string[]
  us: Omit<Provider, 'domain' | 'url'>
  providers: Provider[]
}

const PRICE_MAX = 4
const STAR_MAX = 5

/** The Dimension mark, matching src/assets/dimension-logo-new.svg. */
function DimensionMark() {
  return (
    <svg viewBox='0 0 18 18' aria-hidden='true' focusable='false'>
      <path d='M9.00823 18C11.3922 17.9974 13.6778 17.0492 15.3635 15.3635C17.0492 13.6778 17.9974 11.3922 18 9.00821L18 4.37669L13.6233 4.37669L13.6233 1.52472e-06L8.99177 1.11982e-06C6.60794 0.00304695 4.32258 0.951375 2.63696 2.637C0.951331 4.32263 0.00304131 6.60794 -3.02717e-06 8.99177C0.00260792 11.3801 0.952488 13.6699 2.64129 15.3587C4.3301 17.0475 6.6199 17.9974 9.00823 18ZM8.99177 1.97296L11.6503 1.97296L11.6503 4.37669L8.99835 4.37669C7.77301 4.378 6.5982 4.86533 5.73176 5.73178C4.86531 6.59823 4.37801 7.77301 4.37671 8.99835C4.37845 10.2247 4.86652 11.4003 5.73384 12.2673C6.60117 13.1344 7.77688 13.622 9.00325 13.6233C10.2283 13.6224 11.403 13.1354 12.2692 12.2692C13.1354 11.4029 13.6224 10.2283 13.6233 9.00329L13.6233 6.34965L16.027 6.34965L16.027 9.00821C16.0249 10.869 15.2847 12.6531 13.9689 13.9689C12.6531 15.2847 10.8691 16.0249 9.00823 16.027C7.14303 16.0249 5.35481 15.283 4.03592 13.9641C2.71702 12.6452 1.97514 10.857 1.97296 8.99177C1.97514 7.13093 2.71529 5.34694 4.0311 4.03112C5.34691 2.71531 7.13093 1.97514 8.99177 1.97296ZM11.6503 6.34965L11.6503 9.00329C11.6495 9.70507 11.3703 10.3778 10.8741 10.8741C10.3779 11.3703 9.70503 11.6495 9.00325 11.6503C8.30001 11.6495 7.62581 11.3698 7.12839 10.8727C6.63097 10.3756 6.35098 9.70159 6.34967 8.99835C6.35054 8.29614 6.62984 7.62294 7.12638 7.1264C7.62292 6.62986 8.29614 6.35052 8.99835 6.34965L11.6503 6.34965Z' />
    </svg>
  )
}

/** Dollar signs filled out of four, so the tier reads without a number. */
function Price({ tier }: { tier: number }) {
  return (
    <span className={classes.price} aria-label={`${'$'.repeat(tier)} of ${PRICE_MAX}`}>
      <span className={classes.priceFilled}>{'$'.repeat(tier)}</span>
      {tier < PRICE_MAX && (
        <span className={classes.priceEmpty}>{'$'.repeat(PRICE_MAX - tier)}</span>
      )}
    </span>
  )
}

/**
 * Stars plus the platform the score came from, because the platforms are not
 * comparable: a company-wide Trustpilot score built from hundreds of reviews and
 * a corporate BBB profile with five say different things, and naming the source
 * is what stops the column reading as one like-for-like number.
 *
 * Providers with no rating we could verify get "No public reviews" rather than
 * zero stars — zero stars asserts a bad score where we actually have no data.
 */
function ReviewCell({ review }: { review: Review | null }) {
  if (!review) return <span className={classes.noReviews}>No public reviews</span>

  const filled = Math.round(review.score)
  return (
    <a
      className={classes.review}
      href={review.url}
      target='_blank'
      rel='nofollow noopener noreferrer'
      aria-label={`${review.source} score ${review.score} out of ${STAR_MAX} from ${review.count} reviews`}
    >
      <span className={classes.stars} aria-hidden='true'>
        {Array.from({ length: STAR_MAX }, (_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <i key={i} className={i < filled ? undefined : classes.starsEmpty} />
        ))}
      </span>
      <span className={classes.reviewSource}>{review.source}</span>
    </a>
  )
}

function ComparisonTable({ heading, columns, us, providers }: ComparisonProps) {
  return (
    <section className={classes.section}>
      <div className={classes.inner}>
        <div className={classes.headWrap}>
          <h2 className={classes.heading}>{heading}</h2>
        </div>
        {/*
          Provider names are the page's only outbound links, and they are
          deliberate: naming and linking each competitor is what makes this page
          answer "<vendor> alternative" queries. nofollow so the comparison does
          not hand them authority.
        */}
        <div className={classes.tableScroll}>
          <table className={classes.table}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column} scope='col'>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className={classes.rowUs}>
                <td>
                  <span className={classes.usName}>
                    <DimensionMark /> {us.name}
                  </span>
                </td>
                <td>{us.coverage}</td>
                <td>
                  <Price tier={us.pricing} />
                </td>
                <td>
                  <ReviewCell review={us.review} />
                </td>
                <td>
                  <span className={classes.yes}>{us.bookOnline}</span>
                </td>
              </tr>
              {providers.map((provider) => (
                <tr key={provider.url}>
                  <td>
                    <span className={classes.providerName}>
                      <a href={provider.url} target='_blank' rel='nofollow noopener noreferrer'>
                        {provider.name}
                      </a>
                      <small className={classes.providerDomain}>{provider.domain}</small>
                    </span>
                  </td>
                  <td>{provider.coverage}</td>
                  <td>
                    <Price tier={provider.pricing} />
                  </td>
                  <td>
                    <ReviewCell review={provider.review} />
                  </td>
                  <td>{provider.bookOnline}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

export default ComparisonTable
